import type { ValidationFormState, ZohoAccount, ZohoItem } from '../../types';
import { NumberCell } from '../shared/NumberCell';
import { ClientPicker } from './ClientPicker';
import { useEffect } from 'react';
import { exonerationConnue, useTaxExemptions } from '../../hooks/useTaxExemptions';
// Module PUR de constantes fiscales, partagé avec le serveur : la liste des
// dépenses à TVA non déductible ne doit exister qu'à un seul endroit, sans quoi
// l'écran et la création de facture finiraient par ne plus dire la même chose.
import { tvaNonDeductible } from '../../../server/lib/zoho-france';

type Line = ValidationFormState['lines'][number];

interface Props {
  lines: Line[];
  items: ZohoItem[];
  accounts: ZohoAccount[];
  onChange: (lines: Line[]) => void;
  /**
   * Affiche une colonne « Client » (lookup customer Books) par ligne.
   * À activer pour les factures de sous-traitance Experts
   * (charge_type = "Sous-traitance (experts)").
   */
  showClientColumn?: boolean;
  /**
   * Type de dépense détecté à l'extraction. Sur un transport de personnes, un
   * hébergement ou des frais postaux, une ligne sans TVA n'est pas exonérée :
   * sa TVA est simplement exclue du droit à déduction, et aucun code VATEX ne
   * décrit ce cas. Lui demander un motif ferait saisir une exonération fausse.
   */
  typeDepense?: string | null;
}

/**
 * Tableau éditable des lignes de facture. Inputs natifs avec <datalist>
 * pour les colonnes Produit et Compte → recherche/autocomplétion par le
 * navigateur, bien plus performante qu'un select à 1000+ options.
 *
 * Largeurs : Description prend l'espace ; les colonnes numériques sont
 * compactes ; Produit/Compte sont confortables. Le wrapper a overflow-x-auto
 * pour rester utilisable sur écran étroit.
 */
export function LineItemsTable({
  lines,
  items,
  accounts,
  onChange,
  showClientColumn,
  typeDepense,
}: Props) {
  const { exemptions: exonerations, parCompte } = useTaxExemptions();

  // Facture MIXTE : au moins une ligne taxée. C'est le seul cas où une ligne
  // sans TVA doit s'expliquer — sur une facture entièrement sans TVA, c'est le
  // régime de la facture (étranger, franchise en base) qui tranche, et
  // demander un motif par ligne ne ferait qu'alourdir la saisie.
  const factureMixte = lines.some((l) => (l.vat_rate ?? 0) > 0);

  // Une note d'hôtel avec petit-déjeuner est mixte (nuitée sans TVA, repas à
  // 10 %) sans qu'aucune ligne ne soit exonérée : la TVA de la nuitée existe,
  // elle n'est simplement pas déductible. On ne demande donc rien.
  const motifAExiger = factureMixte && !tvaNonDeductible(typeDepense);

  const update = (idx: number, patch: Partial<Line>) => {
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  // Pré-remplissage depuis la mémoire : « sur ce compte-là, c'est ce motif-là ».
  // On ne remplit que les cases VIDES — un choix fait à l'écran ne doit jamais
  // être écrasé par la mémoire, sinon corriger devient impossible.
  useEffect(() => {
    if (!motifAExiger || Object.keys(parCompte).length === 0) return;
    let change = false;
    const remplies = lines.map((l) => {
      if ((l.vat_rate ?? 0) > 0 || l.tax_exemption_code) return l;
      const connu = exonerationConnue(parCompte, l.account);
      if (!connu) return l;
      change = true;
      return { ...l, tax_exemption_code: connu };
    });
    if (change) onChange(remplies);
  }, [motifAExiger, parCompte, lines, onChange]);

  const remove = (idx: number) => onChange(lines.filter((_, i) => i !== idx));

  const add = () =>
    onChange([
      ...lines,
      {
        description: '',
        quantity: 1,
        unit_price_ht: 0,
        vat_rate: 20,
        account: '',
      },
    ]);

  // Arrondi PAR LIGNE (au centime) puis somme — c'est l'algorithme utilisé
  // par Zoho Books côté création de bill ainsi que par la plupart des
  // tickets/factures imprimés (TVA détaillée par taux puis sommée).
  // Une sommation sans arrondi par ligne peut introduire un écart de
  // 1 ct sur le total, qui induit l'utilisateur à ajouter une ligne
  // d'écart inutile.
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const totals = lines.reduce(
    (acc, l) => {
      const ht = round2(l.quantity * l.unit_price_ht);
      const vat = round2(ht * ((l.vat_rate ?? 0) / 100));
      return { ht: acc.ht + ht, vat: acc.vat + vat };
    },
    { ht: 0, vat: 0 },
  );

  // Quand l'utilisateur sélectionne un produit Zoho via la datalist, on tente
  // d'associer l'item_id ET de propager le COMPTE D'ACHAT du produit dans
  // line.account. C'est le compte purchase_account_name de Zoho qui est
  // pertinent ici (pas le compte de vente account_name qui est le compte de
  // refacturation client).
  const handleProductChange = (idx: number, productName: string) => {
    // Match exact d'abord, puis insensible à la casse et aux espaces : la
    // datalist recopie parfois une graphie légèrement différente (« KLive
    // Sous-traitants » vs « KLive Sous-Traitants »), et une ligne sans item_id
    // partait à Zoho sans compte d'imputation.
    const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
    const target = normalize(productName);
    const matched =
      items.find((i) => i.name === productName) ?? items.find((i) => normalize(i.name) === target);
    const purchaseAccount =
      matched?.purchase_account_name ||
      // fallback : si purchase_account_name absent, on retombe sur account_name
      matched?.account_name ||
      '';
    update(idx, {
      zoho_item_id: matched?.id ?? null,
      zoho_item_name: productName,
      // On remplace le compte par celui du nouveau produit. Pour ne pas
      // écraser un compte saisi manuellement quand l'utilisateur tape
      // juste un texte libre (pas de match dans la datalist), on garde
      // l'ancien compte si aucun item n'a été matché.
      account: matched ? purchaseAccount : lines[idx]?.account || '',
    });
  };

  const handleAccountChange = (idx: number, accountName: string) => {
    update(idx, { account: accountName });
  };

  return (
    <div className="space-y-2">
      {/* Datalists globales : une seule fois dans le DOM, partagées par toutes les lignes. */}
      <datalist id="zoho-items-list">
        {items.map((i) => (
          <option key={i.id} value={i.name}>
            {i.account_name ?? ''}
          </option>
        ))}
      </datalist>
      <datalist id="zoho-accounts-list">
        {accounts.map((a) => (
          <option key={a.id} value={a.name}>
            {a.code ?? ''}
          </option>
        ))}
      </datalist>

      {/*
       * `min-w-[1040px]` sur la table garantit que chaque colonne ait sa
       * largeur cible même sur mobile : le wrapper overflow-x-auto fait
       * scroller horizontalement plutôt que d'écraser les valeurs. La somme
       * = 240 + 200 + 200 + 100 + 130 + 100 + 40 + paddings.
       */}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className={`${showClientColumn ? 'min-w-[1260px]' : 'min-w-[1040px]'} w-full text-sm`}>
          <colgroup>
            <col style={{ width: '240px', minWidth: '240px' }} />
            <col style={{ width: '200px', minWidth: '180px' }} />
            <col style={{ width: '200px', minWidth: '180px' }} />
            {showClientColumn && <col style={{ width: '220px', minWidth: '200px' }} />}
            <col style={{ width: '100px', minWidth: '90px' }} />
            <col style={{ width: '130px', minWidth: '120px' }} />
            <col style={{ width: '100px', minWidth: '90px' }} />
            <col style={{ width: '40px', minWidth: '36px' }} />
          </colgroup>
          <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-2 py-1.5 font-medium">Description</th>
              <th className="px-2 py-1.5 font-medium">Produit (Zoho)</th>
              <th className="px-2 py-1.5 font-medium">Compte (Zoho)</th>
              {showClientColumn && (
                <th className="px-2 py-1.5 font-medium">
                  Client <span className="text-red-500">*</span>
                </th>
              )}
              <th className="px-2 py-1.5 font-medium text-right">Qté</th>
              <th className="px-2 py-1.5 font-medium text-right">PU HT</th>
              <th className="px-2 py-1.5 font-medium text-right">TVA %</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {lines.map((l, idx) => (
              <tr key={idx} className="align-top">
                {/* Description (facultative, vide par défaut). Texte complet
                    visible au survol via l'attribut title ; redimensionnable. */}
                <td className="px-1 py-1">
                  <textarea
                    value={l.description}
                    onChange={(e) => update(idx, { description: e.target.value })}
                    rows={2}
                    title={l.description || undefined}
                    placeholder="- (facultatif)"
                    className="w-full rounded px-2 py-1 text-sm leading-snug border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none resize-y min-h-[44px]"
                  />
                </td>

                {/* Produit Zoho : bordure ambre si le texte saisi ne correspond
                    à aucun article (la ligne partirait sans item_id). */}
                <td className="px-1 py-1">
                  <input
                    type="text"
                    list="zoho-items-list"
                    value={l.zoho_item_name ?? ''}
                    onChange={(e) => handleProductChange(idx, e.target.value)}
                    placeholder="-"
                    title={
                      l.zoho_item_name && !l.zoho_item_id
                        ? 'Article Zoho non reconnu : resélectionne-le dans la liste'
                        : undefined
                    }
                    className={`w-full rounded px-2 py-1 text-sm border focus:border-blue-500 focus:outline-none ${
                      l.zoho_item_name && !l.zoho_item_id
                        ? 'border-amber-400 bg-amber-50'
                        : 'border-transparent hover:border-slate-200'
                    }`}
                  />
                </td>

                {/* Compte Zoho */}
                <td className="px-1 py-1">
                  <input
                    type="text"
                    list="zoho-accounts-list"
                    value={l.account}
                    onChange={(e) => handleAccountChange(idx, e.target.value)}
                    placeholder="-"
                    className="w-full rounded px-2 py-1 text-sm border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none"
                  />
                </td>

                {/* Client (sous-traitance uniquement) */}
                {showClientColumn && (
                  <td className="px-1 py-1">
                    <ClientPicker
                      value={{
                        id: l.client_books_id ?? null,
                        name: l.client_name ?? null,
                      }}
                      onChange={({ id, name }) =>
                        update(idx, { client_books_id: id, client_name: name })
                      }
                      onClear={() => update(idx, { client_books_id: null, client_name: null })}
                      required
                    />
                  </td>
                )}

                {/* Qté */}
                <td className="px-1 py-1">
                  <NumberCell
                    value={l.quantity}
                    onChange={(v) => update(idx, { quantity: v ?? 0 })}
                  />
                </td>

                {/* PU HT */}
                <td className="px-1 py-1">
                  <NumberCell
                    value={l.unit_price_ht}
                    onChange={(v) => update(idx, { unit_price_ht: v ?? 0 })}
                  />
                </td>

                {/* TVA % — et, si la ligne n'en porte pas sur une facture par
                    ailleurs taxée, le motif d'exonération. Zoho exige l'un ou
                    l'autre depuis l'édition France : sans rien, il retombe
                    silencieusement sur « Exonéré », ce qui est faux. */}
                <td className="px-1 py-1">
                  <NumberCell
                    value={l.vat_rate ?? null}
                    onChange={(v) => update(idx, { vat_rate: v })}
                    allowNull
                    placeholder="-"
                  />
                  {motifAExiger && !(l.vat_rate && l.vat_rate > 0) && (
                    <select
                      value={l.tax_exemption_code ?? ''}
                      onChange={(e) => update(idx, { tax_exemption_code: e.target.value || null })}
                      title="Motif d’exonération de cette ligne"
                      className={`mt-1 w-full rounded border px-1 py-0.5 text-xs ${
                        l.tax_exemption_code
                          ? 'border-slate-300 text-slate-700'
                          : 'border-amber-400 bg-amber-50 text-amber-800'
                      }`}
                    >
                      <option value="">Exonération à préciser</option>
                      {exonerations.map((e) => (
                        <option key={e.code} value={e.code} title={e.aide ?? undefined}>
                          {e.aide ? `${e.aide} — ${e.libelle}` : e.libelle}
                        </option>
                      ))}
                    </select>
                  )}
                </td>

                {/* Suppression */}
                <td className="px-1 py-1 text-right">
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    aria-label="Supprimer la ligne"
                    className="text-slate-400 hover:text-red-600 text-xl leading-none"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {lines.length === 0 && (
              <tr>
                <td colSpan={showClientColumn ? 8 : 7} className="px-4 py-6 text-center text-slate-400 italic">
                  Aucune ligne. Cliquez sur « Ajouter une ligne ».
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={add}
          className="text-sm text-blue-600 hover:underline"
        >
          + Ajouter une ligne
        </button>
        <div className="text-sm text-slate-700 space-x-4 tabular-nums">
          <span>Total HT&nbsp;: <strong>{totals.ht.toFixed(2)} €</strong></span>
          <span>TVA&nbsp;: <strong>{totals.vat.toFixed(2)} €</strong></span>
          <span>Total TTC&nbsp;: <strong>{(totals.ht + totals.vat).toFixed(2)} €</strong></span>
        </div>
      </div>
    </div>
  );
}
